import { Component, ElementRef, EventEmitter, Input, Output, Renderer2, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyFormFieldAppearance as MatFormFieldAppearance } from '@angular/material/legacy-form-field';
import type { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { AbstractFormFieldComponent } from '@shared/form-fields/abstract-form-field/abstract-form-field.component';

import { makeProvider } from '@shared/form-fields/make-provider';
import type { AppSelectOptionGroup } from '@shared/form-fields/options/grouped-select-options';
import type { AppSelectOption } from '@shared/form-fields/options/select-option';
import type { AppSelectChange } from '@shared/form-fields/select/app-select-change';
import { combineLatest } from 'rxjs';
import { Subject } from 'rxjs/internal/Subject';
import { map, startWith } from 'rxjs/operators';

export type CompareWith<T> = (a: AppSelectOption<T>, b: AppSelectOption<T>) => boolean;

// language=Angular2Html
export const template = `
    <mat-form-field [appearance]="appearance">
        <mat-label *ngIf="label">
            {{ label }}
        </mat-label>

        <mat-select [(ngModel)]="selectedOption"
                    [ngModelOptions]="{ standalone: true }"
                    [placeholder]="placeholder"
                    [required]="required"
                    [disabled]="disabled"
                    (opened)="_focusSearchInput()"
                    (selectionChange)="_onOptionSelected($event)"
                    [compareWith]="_compareWith">
            <mat-select-trigger>
                <app-select-option *ngIf="selectedOption"
                                   [option]="selectedOption">
                </app-select-option>
            </mat-select-trigger>

            <div *ngIf="searchEnabled"
                 class="app-form-select-search-container">
                <mat-icon>search</mat-icon>

                <input type="text"
                       #searchInput
                       [formControl]="searchCtrl"
                       [placeholder]="searchPlaceholder"
                       data-testid="input:dropdown-search"
                       autofocus>

                <button cll-icon-button
                        *ngIf="searchCtrl.value?.length"
                        (click)="searchCtrl.setValue(null)">
                    <cll-icon>clear</cll-icon>
                </button>
            </div>

            <ng-container *ngFor="let group of filteredOptions$ | async">
                <mat-optgroup *ngIf="(group.options | arrayFilter:isNotExcluded).length"
                              [label]="group.name">

                    <ng-container *ngFor="let option of (group.options | arrayFilter:isNotExcluded)">
                        <mat-option
                            [value]="option"
                            [disabled]="option.disabled"
                            [class.has-avatar]="option.has_avatar || option.avatar_url"
                            [attr.data-testid]="option.label">
                            <app-select-option [option]="option"></app-select-option>
                        </mat-option>
                    </ng-container>
                </mat-optgroup>
            </ng-container>

            <mat-option *ngIf="!(filteredOptions$ | async)?.length"
                        [disabled]="true">
                No options available
            </mat-option>
        </mat-select>

        <div matSuffix>
            <ng-content select="[appSuffix]"></ng-content>

            <mat-spinner [diameter]="20"
                         *ngIf="fetching"
                         cllTooltip="Fetching data">
            </mat-spinner>
        </div>

        <button matSuffix
                class="clear-button"
                *ngIf="clearable"
                cll-icon-button
                (click)="clear($event)">
            <cll-icon>clear</cll-icon>
        </button>

        <mat-hint *ngIf="hint">
            {{ hint }}
        </mat-hint>

        <mat-error *ngFor="let message of errorMessages">
            {{ message }}
        </mat-error>
    </mat-form-field>
`;

@Component({
    selector: 'app-grouped-select-form-field',
    styleUrls: ['./grouped-select-form-field.component.scss'],
    template,
    providers: [makeProvider(GroupedSelectFormFieldComponent)],
})
export class GroupedSelectFormFieldComponent<T> extends AbstractFormFieldComponent<T> {
    @ViewChild('searchInput', { read: ElementRef }) searchInput: ElementRef;

    @Input() appearance: MatFormFieldAppearance = 'outline';
    @Input() placeholder: string;
    @Input() required: boolean;
    @Input() compareWith: CompareWith<T>;
    @Input() protected objectEqualityKey = 'id';
    @Input() searchEnabled = false;
    @Input() searchPlaceholder = 'Search';

    @Input() exclude: T[];

    @Output() selectionChange = new EventEmitter<AppSelectChange<T>>();
    @Output() fetchingChange = new EventEmitter<boolean>();

    protected _options: AppSelectOptionGroup[];
    protected _apiOptions: AppSelectOptionGroup[];

    @Input() set options(value: AppSelectOptionGroup[]) {
        if (this.value) {
            this.selectedOption = this.findSelectedOption(this.value, value);
        }

        this._options = value;
        this._apiOptions = null;

        // Template won't update unless wrapped in requestAnimationFrame
        requestAnimationFrame(() => this.triggerFilter.next(true));
    }

    get options(): AppSelectOptionGroup[] {
        return this._apiOptions || this._options;
    }

    @Output() valueChanged: EventEmitter<AppSelectOption<T>> = new EventEmitter();

    protected set apiOptions(options: AppSelectOptionGroup[]) {
        this._apiOptions = options;

        if (this._selectedOption) {
            this._selectedOption = this.findSelectedOption(this._selectedOption.value, this.options);
        }

        // Template won't update unless wrapped in requestAnimationFrame
        requestAnimationFrame(() => this.triggerFilter.next(true));
    }

    private _selectedOption: AppSelectOption<T>;

    get selectedOption(): AppSelectOption<T> {
        return this._selectedOption;
    }

    set selectedOption(value: AppSelectOption<T>) {
        this._selectedOption = value;
        this.valueChanged.emit(value);

        this.value = value ? value.value : undefined;
    }

    // Search
    searchCtrl = new UntypedFormControl();

    // Allow the filtering to be triggered manually when options are updated
    triggerFilter = new Subject<true>();
    filteredOptions$ = combineLatest([
        // Make sure the searchCtrl emits null to start with so combineLatest can begin emitting
        this.searchCtrl.valueChanges.pipe(startWith(null)),
        this.triggerFilter
    ]).pipe(
        map(([query]) => filterOptions(this.options, query))
    );

    private _fetching = false;

    get fetching(): boolean {
        return this._fetching;
    }

    set fetching(value: boolean) {
        this._fetching = value;

        this.fetchingChange.emit(value);
    }

    get isDisabled() {
        return this.disabled || this._fetching;
    }

    constructor(element: ElementRef, renderer: Renderer2) {
        super(element, renderer);

        this._compareWith = this._compareWith.bind(this);
        this.isNotExcluded = this.isNotExcluded.bind(this);
    }

    writeValue(value: T) {
        super.writeValue(value);

        if (value !== undefined) {
            this._selectedOption = {
                label: undefined,
                value
            };
        } else {
            this._selectedOption = undefined;
        }

        if (this._selectedOption && this.options) {
            this.selectedOption = this.findSelectedOption(value, this.options);
        }
    }

    public _compareWith(a: AppSelectOption<T>, b: AppSelectOption<T>): boolean {
        if (this.compareWith) {
            return this.compareWith(a, b);
        }

        if (a === b) {
            return true;
        }

        if (a && b) {
            const aValue = a.value;
            const bValue = b.value;

            if (aValue === bValue) {
                return true;
            }

            if (aValue && bValue) {
                return (
                    typeof aValue === 'object'
                    && typeof bValue === 'object'
                    && aValue[this.objectEqualityKey] === bValue[this.objectEqualityKey]
                );
            }
        }

        return false;
    }

    clear($event: MouseEvent) {
        super.clear($event);

        this._selectedOption = undefined;
    }

    isNotExcluded(option: AppSelectOption<T>): boolean {
        if (this.exclude?.length) {
            return this.exclude.findIndex(value => this._compareWith(option, { label: null, value })) === -1;
        }

        return true;
    }

    _focusSearchInput() {
        // Focus the search input
        requestAnimationFrame(() => this.searchInput?.nativeElement?.focus());
    }

    private findSelectedOption(value: T, groups: AppSelectOptionGroup<T>[]) {
        return groups.flatMap(group => group.options)
            .find(option => this._compareWith({ label: null, value }, option));
    }

    _onOptionSelected($event: MatSelectChange) {
        this.selectionChange.emit({
            source: $event.source,
            option: $event.value.value as T,
        });
    }
}

function filterOptions<T>(groups: AppSelectOptionGroup<T>[], search: string): AppSelectOptionGroup<T>[] {
    if (!search?.length || !groups?.length) {
        return groups;
    }

    return (groups ?? []).map(group => ({
        ...group,
        options: group.options.filter(option => option.label.toLowerCase().includes(search.toLowerCase()))
    })).filter(group => group.options.length);
}
